"use client";

import { Suspense, useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";

/**
 * Fire and forget pageview beacon for the self-hosted analytics.
 *
 * Sends nothing but the path and the referrer origin. Rendering never waits
 * on it: the send is deferred past paint and uses sendBeacon where available,
 * so it cannot block navigation or hold the main thread.
 */

const IGNORED_PREFIXES = ["/api/", "/_next/", "/static/", "/admin"];

const STATIC_EXTENSION = /\.[a-z0-9]{2,5}$/i;

function shouldTrack(path: string): boolean {
  if (!path.startsWith("/")) return false;
  if (IGNORED_PREFIXES.some((prefix) => path.startsWith(prefix))) return false;
  if (path !== "/" && STATIC_EXTENSION.test(path)) return false;
  return true;
}

function isLikelyBot(): boolean {
  if (typeof navigator === "undefined") return true;
  if (navigator.webdriver) return true;
  return /bot|crawl|spider|headlesschrome|lighthouse/i.test(navigator.userAgent);
}

function send(path: string) {
  const payload = JSON.stringify({
    path,
    referrer: document.referrer || null,
  });

  try {
    if (typeof navigator.sendBeacon === "function") {
      // sendBeacon is queued by the browser and survives the page unloading.
      const blob = new Blob([payload], { type: "application/json" });
      if (navigator.sendBeacon("/api/analytics/pageview", blob)) return;
    }
  } catch {
    // fall through to fetch
  }

  void fetch("/api/analytics/pageview", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: payload,
    keepalive: true,
    credentials: "same-origin",
  }).catch(() => {
    // Analytics failures are silent by design.
  });
}

function PageviewTrackerInner() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastSent = useRef<string | null>(null);

  // The query string is never sent, but it is part of the key so that a
  // filter change still counts as a fresh view of the same page.
  const key = `${pathname}?${searchParams.toString()}`;

  useEffect(() => {
    if (!pathname || !shouldTrack(pathname)) return;
    if (lastSent.current === key) return;
    if (isLikelyBot()) return;

    lastSent.current = key;

    // Defer past the current frame so tracking never competes with paint.
    const schedule =
      typeof window.requestIdleCallback === "function"
        ? window.requestIdleCallback
        : (cb: () => void) => window.setTimeout(cb, 300);

    const handle = schedule(() => send(pathname));

    return () => {
      if (typeof window.cancelIdleCallback === "function" && typeof handle === "number") {
        window.cancelIdleCallback(handle);
      } else if (typeof handle === "number") {
        window.clearTimeout(handle);
      }
    };
  }, [key, pathname]);

  return null;
}

/**
 * Wrapped in Suspense because useSearchParams opts a subtree into client side
 * rendering. Keeping the boundary here means the mounting layout needs no
 * extra wiring and no part of the page is held back by it.
 */
export function PageviewTracker() {
  return (
    <Suspense fallback={null}>
      <PageviewTrackerInner />
    </Suspense>
  );
}
